import {
  TextManager,
  getCollectionOfDittoComponents,
  getComponentTextByFieldId,
} from "Components/TextContext";
import { TextComponentNameLevel1 } from "Components/TextContext/textConstants";
import { INSURANCE_NAME_VALUE } from "Store/interfaces/products";
import { ProductType } from "Store/interfaces/products/checkout/ProductType";
import { ChildProduct } from "Store/interfaces/products/checkout/lifeProducts/ChildProduct";
import { PregnantAndNewbornProduct } from "Store/interfaces/products/checkout/lifeProducts/PregnantAndNewbornProduct";
import {
  AnimalProduct,
  Species,
} from "Store/interfaces/products/checkout/riskProducts/AnimalProduct";
import {
  ChildCoverage,
  PregnantAndNewbornCoverage,
} from "Store/interfaces/products/coverage";
import { isManualProduct } from "Utils/productUtils";

export const getProductName = (product: ProductType): string => {
  const products = getCollectionOfDittoComponents(
    TextComponentNameLevel1.PRODUCTS,
  );
  const { getText } = TextManager;

  const formattedProductName = getFormattedProductName(product);

  switch (product.name) {
    case INSURANCE_NAME_VALUE.AKA_HEALTH_WITHOUT_SIGNING:
      return getText("insuranceproduct.akahealth.name");
    case INSURANCE_NAME_VALUE.HEALTH_WITHOUT_SIGNING:
      return getText("insuranceproduct.health.name");
    case INSURANCE_NAME_VALUE.ANIMAL:
      return getAnimalProductName(product);
    case INSURANCE_NAME_VALUE.PREGNANT_AND_NEWBORN:
      return getPregnantAndNewbornProductName(product);
    case INSURANCE_NAME_VALUE.NEW_CHILD:
      return getNewChildProductName(product);
  }

  const productName = getComponentTextByFieldId(
    products[formattedProductName],
    "name",
  );

  if (productName === undefined) {
    return "";
  }

  return productName;
};

export const getAnimalProductName = (
  product: Pick<AnimalProduct, "name" | "species">,
): string => {
  const { getText } = TextManager;
  switch (product.species) {
    case Species.DOG:
      return getText("insuranceproduct.animal.namedog");
    case Species.CAT:
      return getText("insuranceproduct.animal.namecat");
    default:
      return getText("insuranceproduct.animal.name");
  }
};

export const getPregnantAndNewbornProductName = (
  product: Pick<PregnantAndNewbornProduct, "name" | "coverageGroup">,
): string => {
  const { getText } = TextManager;
  switch (product.coverageGroup) {
    case PregnantAndNewbornCoverage.NEWBORN:
      return getText("insuranceproduct.pregnantandnewborn.namenewborn");
    case PregnantAndNewbornCoverage.NEWBORN_MULTIPLE_CHILDREN:
      return getText(
        "insuranceproduct.pregnantandnewborn.namenewbornmultiplechildren",
      );
    case PregnantAndNewbornCoverage.PREGNANT:
      return getText("insuranceproduct.pregnantandnewborn.namepregnant");
    case PregnantAndNewbornCoverage.PREGNANT_MULTIPLE_CHILDREN:
      return getText(
        "insuranceproduct.pregnantandnewborn.namepregnantmultiplechildren",
      );
    default:
      return getText("insuranceproduct.pregnantandnewborn.name");
  }
};

const getNewChildProductName = (
  product: Pick<ChildProduct, "name" | "coverageGroup">,
): string => {
  const { getText } = TextManager;
  switch (product.coverageGroup) {
    case ChildCoverage.TOTAL:
      return getText("insuranceproduct.newchild.total");
    case ChildCoverage.EXTRA_HALF_G:
    case ChildCoverage.EXTRA:
      return getText("insuranceproduct.newchild.extra");
    case ChildCoverage.BASIC:
      return getText("insuranceproduct.newchild.basic");
    default:
      return getText("insuranceproduct.newchild.name");
  }
};

export const formatProductName = (productName: INSURANCE_NAME_VALUE): string =>
  productName.replace(/\_/g, "").toLowerCase();

const getFormattedProductName = (product: ProductType): string =>
  isManualProduct(product)
    ? formatProductName(product.insuranceName)
    : formatProductName(product.name);
